import React from "react";
import { Button, Select } from "components";
import { usePropertyContext } from "../../context";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { ExemptionCurrentlyReceivingValues } from "utils/types";
import ButtonV2 from "elements/ButtonV2";
import { property_has_viable_exemption } from "../questionFlowMachines/research.machine";

const CurrentlyReceiving = () => {
	const { details } = usePropertyContext();
	const { currentlyReceivingValue, setCurrentlyReceivingValue, next, flowId } =
		useExemptionsContext();
	const trackEvent = useEventTracker();

	const options = React.useMemo(() => {
		if (flowId === "CALIFORNIA_EXEMPTION") {
			return [
				{ label: "No", value: "no" },
				{ label: "Not sure", value: "notSure" },
				{
					label: "Yes, on another property in California",
					value: "yesAnotherPropertyInCalifornia",
				},
				{
					label: "Yes, on another property in another state",
					value: "yesAnotherPropertyInAnotherState",
				},
				{ label: `Yes, here at ${details?.address}`, value: "yes" },
			];
		}

		if (flowId === "NEW_YORK_EXEMPTION") {
			return [
				{ label: "No", value: "no" },
				{ label: "Not sure", value: "notSure" },
				{ label: `Yes, here at ${details?.address}`, value: "yes" },
				{
					label: "Yes, on another property in New York",
					value: "yesAnotherPropertyInNewYork",
				},
				{
					label: "Yes, on another property in another state",
					value: "yesAnotherPropertyInAnotherState",
				},
			];
		}

		if (flowId === "FLORIDA_EXEMPTION") {
			return [
				{ label: "No", value: "no" },
				{ label: "Not sure", value: "notSure" },
				{ label: `Yes, here at ${details?.address}`, value: "yes" },
			];
		}

		if (flowId === "TEXAS_EXEMPTION") {
			return [
				{ label: "No", value: "no" },
				{ label: "Not sure", value: "notSure" },
				{ label: "Yes, on another property", value: "yesAnotherProperty" },
				{ label: `Yes, here at ${details?.address}`, value: "yes" },
				{
					label: "Yes, but it was recently removed and I want it reinstated",
					value: "yesButRecentlyRemoved",
				},
			];
		}

		return [
			{ label: "No", value: "no" },
			{ label: "Not sure", value: "notSure" },
			{ label: "Yes, on another property", value: "yesAnotherProperty" },
			{ label: `Yes, here at ${details?.address}`, value: "yes" },
		];
	}, [flowId, details?.address]);

	const handleSubmit = async () => {
		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Current_Homestead_Answer,
			data: {
				"Current Homestead": currentlyReceivingValue,
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
				State: details?.state,
			},
		});
		const trackingEventData = {
			"Current Homestead": currentlyReceivingValue,
			State: details?.state,
		};
		next({ trackingEventData });
	};

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				{flowId === "NEW_YORK_EXEMPTION" && (
					<>
						<div className="property-profile-question-card-title-container">
							<p>
								Are you currently receiving an exemption or credit at this or
								another property?
							</p>
						</div>
						<p className="exemption-question-card-subtitle sm">
							You will only be allowed to have an exemption or credit on one
							property at a time.
						</p>
					</>
				)}
				{flowId === "CALIFORNIA_EXEMPTION" && (
					<>
						<div className="property-profile-question-card-title-container">
							<p>
								Are you currently receiving any exemptions at this or another
								property?
							</p>
						</div>
						<p className="exemption-question-card-subtitle sm">
							Most exemptions can only be applied to one property at a time.
						</p>
					</>
				)}
				{[
					"ILLINOIS_EXEMPTION",
					"GEORGIA_EXEMPTION",
					"TEXAS_EXEMPTION",
				].includes(flowId ?? "") && (
					<>
						<div className="property-profile-question-card-title-container">
							<p>
								Are you currently receiving a homestead exemption at this or
								another property?
							</p>
						</div>
						<p className="exemption-question-card-subtitle sm">
							You will only be allowed to have a homestead exemption on one
							property at a time.
						</p>
					</>
				)}
				{flowId === "FLORIDA_EXEMPTION" && (
					<>
						<div className="property-profile-question-card-title-container">
							<p>
								Are any owners currently receiving a homestead exemption at this
								property?
							</p>
						</div>
						<p className="exemption-question-card-subtitle sm">
							You will only be allowed to have a homestead exemption on one
							property at a time.
						</p>
					</>
				)}
				{["MICHIGAN_EXEMPTION", "PENNSYLVANIA_EXEMPTION"].includes(
					flowId ?? ""
				) && (
					<>
						<div className="property-profile-question-card-title-container">
							<p>
								Are you currently receiving a primary residence exemption at
								this or another property?
							</p>
						</div>
						<p className="exemption-question-card-subtitle sm">
							You will only be allowed to have a primary residence exemption on
							one property at a time.
						</p>
					</>
				)}
				{flowId === "RESEARCH_EXEMPTION" && (
					<>
						<div className="property-profile-question-card-title-container">
							<p>
								Are you currently receiving a{" "}
								{property_has_viable_exemption(details)
									? "homestead"
									: "primary residence"}{" "}
								exemption at this or another property?
							</p>
						</div>
						<p className="exemption-question-card-subtitle sm">
							You will only be allowed to have a homestead exemption on one
							property at a time.
						</p>
					</>
				)}
				<Select
					required
					className="exemptions-card-input"
					value={currentlyReceivingValue ?? ""}
					onChange={e =>
						setCurrentlyReceivingValue(
							e.target.value as ExemptionCurrentlyReceivingValues
						)
					}>
					<option value="" hidden disabled>
						Select an Answer
					</option>
					{options.map(({ label, value }) => (
						<option key={label} value={value}>
							{label}
						</option>
					))}
				</Select>
			</div>
			{currentlyReceivingValue === "yes" && flowId !== "NEW_YORK_EXEMPTION" && (
				<p className="sm exemptions-notification-message-azure">
					You're already saving money! Since you have an active homestead
					exemption at this residence, we will move on to some other savings
					opportunities.
				</p>
			)}
			{flowId === "TEXAS_EXEMPTION" &&
				currentlyReceivingValue === "yesAnotherProperty" && (
					<p className="sm exemptions-notification-message-rust">
						We are not able to support Homestead transfers at this time, so you
						will need to contact the county to determine what steps need to be
						taken to properly transfer from one address to another.
					</p>
				)}
			{flowId === "NEW_YORK_EXEMPTION" &&
				currentlyReceivingValue === "yesAnotherPropertyInAnotherState" && (
					<p className="sm exemptions-notification-message-azure">
						We are not able to support exemption transfers for properties in
						another state at this time. You will need to contact the county to
						determine what steps need to be taken to properly transfer from one
						address to another.
					</p>
				)}
			{flowId === "ILLINOIS_EXEMPTION" &&
				currentlyReceivingValue === "yesAnotherProperty" && (
					<p className="sm exemptions-notification-message-rust">
						We currently do not support Exemption transfers at this time.{" "}
						However, you can still transfer your exemption yourself by visiting
						your County Appraisal District website. There it will explain the
						steps you need to take. You can proceed on to the next step and we
						will determine if there are other ways we can help you save!
					</p>
				)}
			{flowId === "PENNSYLVANIA_EXEMPTION" &&
				currentlyReceivingValue === "yesAnotherProperty" && (
					<p className="sm exemptions-notification-message-rust">
						We are not able to support Homestead transfers at this time. You
						will need to contact the county to determine what steps need to be
						taken to properly transfer from one address to another.
					</p>
				)}
			{flowId === "TEXAS_EXEMPTION" && !!details?.homestead_fell_off_at && (
				<p className="sm exemptions-notification-message-rust flex flex-column flex-gap-0_5">
					<u>Recent Fall Off Monitoring Alert</u>
					It looks like your primary residence exemption may have recently
					fallen off. If this is the case and you want to get your exemption
					reinstated, please choose "Yes, but it was recently removed and I want
					it reinstated" so that we can help you get things back on track.
				</p>
			)}
			<ButtonV2
				size="large"
				disabled={currentlyReceivingValue === undefined}
				type="submit">
				Continue
			</ButtonV2>
		</form>
	);
};

export default CurrentlyReceiving;
